import * as Popover from '@radix-ui/react-popover'
import { ArrowUpRight } from '@/assets/icons'
import { useState, useRef, useEffect, useContext, useCallback } from 'react'
import { PopoverContainerContext } from '@/context/popover-container-context'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import { useMatterAssetFeatureConfig } from '@/matter-asset-common/feature/matter-asset-feature-context'
import type { MatterAssetReference } from '@/matter-asset-common/feature/matter-asset-feature-config'

// Minimum intersection ratio for the trigger to be considered visible
// Accounts for the table's sticky header which can partially obscure the trigger
const MIN_TRIGGER_VISIBILITY_RATIO = 0.6

interface ViewSourcePopoverProps {
  reference: MatterAssetReference
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
  isCellHovered?: boolean
  allowCellHover?: boolean // opt in to allow cell hover to trigger the popover
  onItemHighlightChange?: (isItemHovered: boolean) => void // allows wrapped element to update its highlight state, synced with popover visibility
  isReferenceActive?: boolean // if true, the reference is currently open in the content viewer
  children: React.ReactElement
}

/**
 * View Source Popover
 *
 * Reusable wrapper that adds "View source" hover/tap behavior to any referenced element.
 * It separates popover logic from rendering, allowing any content to be wrapped
 * with consistent "View source" hover/tap behavior.
 *
 * Can be opened from:
 * - Item-level hover/tap (when the wrapped element itself is hovered/tapped), or
 * - Cell-level hover (desktop only, if opted in via `allowCellHover` prop)
 *
 * Used in table cells to open the source viewer for a reference. Opening (highlights + the
 * reference-viewer-opened analytics) is delegated to the active feature config's `useOpenSource`.
 */
export default function ViewSourcePopover(props: ViewSourcePopoverProps) {
  const {
    reference,
    cellPosition,
    scrollToCell,
    isCellHovered = false,
    allowCellHover = false,
    onItemHighlightChange,
    isReferenceActive = false,
    children,
  } = props
  const config = useMatterAssetFeatureConfig()
  const isTouchOnlyDevice = useIsTouchOnlyDevice()
  const { openReferenceSource, sourceLoadError } = config.useOpenSource()

  // Local UI state
  const [showPopover, setShowPopover] = useState(false)
  const [isItemHovered, setIsItemHovered] = useState(false)
  const [isTriggerVisible, setIsTriggerVisible] = useState(false)

  // Refs
  const containerEl = useContext(PopoverContainerContext)
  const triggerRef = useRef<HTMLElement | null>(null)

  // Derived states
  const isCellHoverActive = allowCellHover && isCellHovered
  const isHovered = isCellHoverActive || isItemHovered
  const shouldShowPopover = isHovered && isTriggerVisible && !isReferenceActive

  // Toggle popover and highlight visibility (the two should always be in sync)
  const togglePopoverWithHighlight = useCallback(
    (open: boolean) => {
      onItemHighlightChange?.(open)
      setShowPopover(open)
    },
    [onItemHighlightChange]
  )

  // Desktop: Debounce popover and highlight visibility to prevent flickering when cursor briefly passes over trigger
  useDebouncedEffect(() => {
    if (isTouchOnlyDevice) return

    togglePopoverWithHighlight(shouldShowPopover)
  }, [shouldShowPopover, onItemHighlightChange, isTouchOnlyDevice])

  /**
   * Observe trigger visibility inside container - closes the popover if the trigger is scrolled out of view
   * UX Goal: Prevent the popover from being rendered if the trigger is not visible in the table
   */
  useEffect(() => {
    if (!triggerRef.current || !containerEl) return
    const io = new IntersectionObserver(
      ([entry]) => {
        // Check if there's sufficient vertical overlap
        const minVisibleHeight = entry.boundingClientRect.height * MIN_TRIGGER_VISIBILITY_RATIO
        const visible = entry.isIntersecting && entry.intersectionRect.height >= minVisibleHeight
        setIsTriggerVisible(visible)
        if (!visible) {
          // Immediately close popover if scrolled out of view
          togglePopoverWithHighlight(false)
        }
      },
      { root: containerEl, threshold: [0, MIN_TRIGGER_VISIBILITY_RATIO] }
    )
    io.observe(triggerRef.current)
    return () => io.disconnect()
  }, [containerEl, togglePopoverWithHighlight])

  const handleViewSource = async () => {
    scrollToCell(cellPosition)
    const opened = await openReferenceSource(reference)

    if (!opened) {
      togglePopoverWithHighlight(true)
      return
    }

    togglePopoverWithHighlight(false)
  }

  const referenceLoadError = sourceLoadError?.sourceId === reference.source.source_id ? sourceLoadError.message : null

  return (
    <Popover.Root
      open={showPopover}
      onOpenChange={(open) => {
        // do not allow opening if the trigger is not visible or if the reference is active
        if (open && (!isTriggerVisible || isReferenceActive)) return
        togglePopoverWithHighlight(open)
      }}
    >
      {isTouchOnlyDevice ? (
        // Touch: Direct popover visibilty control via tap
        <Popover.Trigger asChild>
          <span
            ref={(node) => {
              triggerRef.current = node as HTMLElement | null
            }}
            onClick={() => {
              // On touch devices, tapping the trigger opens the popover - user must tap "View source" button to open file
              togglePopoverWithHighlight(true)
            }}
            className="inline pointer-events-auto"
          >
            {children}
          </span>
        </Popover.Trigger>
      ) : (
        // Desktop: Popover visibility controlled by hover via mouse events
        <Popover.Trigger asChild>
          <span
            ref={(node) => {
              triggerRef.current = node as HTMLElement | null
            }}
            onMouseEnter={() => setIsItemHovered(true)}
            onMouseLeave={() => setIsItemHovered(false)}
            onClick={handleViewSource}
            className="inline"
          >
            {children}
          </span>
        </Popover.Trigger>
      )}

      {/* Pin the portal inside the container when available */}
      <Popover.Portal container={containerEl || undefined}>
        <Popover.Content
          className="rounded bg-white ring-1 ring-inset ring-brand-neutral-300 z-50"
          side="top"
          align="end"
          sideOffset={3}
          alignOffset={-8}
          avoidCollisions
          collisionBoundary={containerEl || undefined}
          collisionPadding={8}
          {...(!isTouchOnlyDevice && {
            onMouseEnter: () => setIsItemHovered(true),
            onMouseLeave: () => setIsItemHovered(false),
          })}
          // Prevent outside interactions from closing the popover if cell hover is active for the item
          onPointerDownOutside={(e) => {
            if (isCellHoverActive && !isTouchOnlyDevice) e.preventDefault()
          }}
          onFocusOutside={(e) => {
            if (isCellHoverActive && !isTouchOnlyDevice) e.preventDefault()
          }}
        >
          <div>
            <button
              onClick={handleViewSource}
              className="flex items-center gap-1 text-xs leading-4 text-brand-neutral-800 cursor-pointer font-medium focus:outline-none py-1 px-2"
            >
              View source
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            {referenceLoadError && <p className="px-2 pb-1 text-xs font-medium text-red-800">{referenceLoadError}</p>}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
